import { useState } from "react";
import { api } from "@/lib/api";
import { fmtMoney } from "@/lib/format";
import { toast } from "sonner";
import { Loader2, Pencil, RefreshCcw, Save, X } from "lucide-react";

const btnCls = "p-1.5 border inline-flex items-center justify-center hover:bg-[var(--ll-mint)] transition-colors disabled:opacity-40 disabled:cursor-not-allowed";
const LABELS = { opening_balance: "beginning", closing_balance: "ending" };

export const SourceTag = ({ source, testid }) => (source ? (
  <span
    data-testid={testid}
    className="ml-1.5 text-[9px] uppercase tracking-widest px-1 py-0.5 align-middle"
    style={source === "manual"
      ? { background: "#FEF3C7", color: "#92400E" }
      : { background: "#E0F2FE", color: "#075985" }}
  >
    {source === "manual" ? "Manual" : "From statement"}
  </span>
) : null);

export default function StatementBalanceActions({ statement, onUpdated, testidPrefix = "statement" }) {
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const id = statement.id;

  const reread = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/statements/${id}/reread-balances`);
      let updated = r.data.statement;
      const conflicts = r.data.manual_conflicts || {};
      const keys = Object.keys(conflicts);
      if (keys.length) {
        const lines = keys.map((k) => `• ${LABELS[k]}: manual ${fmtMoney(conflicts[k].current)} → statement ${fmtMoney(conflicts[k].found)}`);
        if (window.confirm(`Replace manually entered balance(s) with the values read from the statement?\n${lines.join("\n")}`)) {
          const body = { source: "statement" };
          keys.forEach((k) => { body[k] = conflicts[k].found; });
          updated = (await api.patch(`/statements/${id}/balances`, body)).data;
        }
      }
      const { opening_balance: o, closing_balance: c } = r.data.found;
      if (o == null && c == null) toast.warning("No balances could be read from this statement — enter them manually");
      else toast.success(`Read beginning ${o != null ? fmtMoney(o) : "—"} · ending ${c != null ? fmtMoney(c) : "—"}`);
      onUpdated && onUpdated(updated);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Re-read failed");
    } finally { setBusy(false); }
  };

  return (
    <>
      <button
        onClick={reread}
        disabled={busy || !statement.storage_path || statement.status !== "parsed"}
        data-testid={`${testidPrefix}-reread-${id}`}
        title={statement.storage_path ? "Re-read beginning/ending balance from the original file" : "Original file not archived — enter balances manually or re-upload"}
        className={btnCls}
        style={{ borderColor: "var(--ll-border)" }}
      >
        {busy ? <Loader2 size={14} className="animate-spin" /> : <RefreshCcw size={14} />}
      </button>
      <button
        onClick={() => setEditing(true)}
        disabled={statement.status !== "parsed"}
        data-testid={`${testidPrefix}-edit-balances-${id}`}
        title="Edit beginning/ending balance"
        className={btnCls}
        style={{ borderColor: "var(--ll-border)" }}
      >
        <Pencil size={14} />
      </button>
      {editing && (
        <BalancesDialog statement={statement} onClose={() => setEditing(false)} onSaved={(s) => { setEditing(false); onUpdated && onUpdated(s); }} />
      )}
    </>
  );
}

function BalancesDialog({ statement, onClose, onSaved }) {
  const [opening, setOpening] = useState(statement.opening_balance ?? "");
  const [closing, setClosing] = useState(statement.closing_balance ?? "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    const parse = (v) => (String(v).trim() === "" ? null : parseFloat(String(v).replace(/[$,]/g, "")));
    const o = parse(opening);
    const c = parse(closing);
    if ((o !== null && !Number.isFinite(o)) || (c !== null && !Number.isFinite(c))) { toast.error("Enter valid numbers"); return; }
    const body = { source: "manual" };
    if (o !== (statement.opening_balance ?? null)) body.opening_balance = o;
    if (c !== (statement.closing_balance ?? null)) body.closing_balance = c;
    if (Object.keys(body).length === 1) { onClose(); return; }
    setBusy(true);
    try {
      const r = await api.patch(`/statements/${statement.id}/balances`, body);
      toast.success("Statement balances saved");
      onSaved(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/40 grid place-items-center p-4" onClick={onClose} data-testid="statement-balances-dialog">
      <div className="bg-white w-full max-w-sm shadow-xl text-left" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 flex items-center justify-between bg-[var(--ll-ink)] text-white">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest opacity-70">Statement balances</div>
            <div className="font-display font-bold text-sm truncate">{statement.file_name}</div>
          </div>
          <button onClick={onClose} aria-label="Close" data-testid="statement-balances-close"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-4">
          <BalanceField label="Beginning balance" value={opening} onChange={setOpening} source={statement.opening_balance_source} testid="statement-balances-opening" />
          <BalanceField label="Ending balance" value={closing} onChange={setClosing} source={statement.closing_balance_source} testid="statement-balances-closing" />
          {statement.balances_updated_by_name && (
            <div className="text-[11px] text-[var(--ll-ink-soft)]">
              Last changed by {statement.balances_updated_by_name} · {new Date(statement.balances_updated_at).toLocaleString()}
            </div>
          )}
          <button
            onClick={save}
            disabled={busy}
            data-testid="statement-balances-save"
            className="w-full py-2 text-sm text-white bg-[var(--ll-ink)] hover:opacity-90 inline-flex items-center justify-center gap-2 disabled:opacity-50 transition-opacity"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save balances
          </button>
        </div>
      </div>
    </div>
  );
}

const BalanceField = ({ label, value, onChange, source, testid }) => (
  <label className="block">
    <span className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)]">
      {label}<SourceTag source={source} testid={`${testid}-source`} />
    </span>
    <input
      type="text"
      inputMode="decimal"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Not set"
      data-testid={testid}
      className="mt-1 w-full px-3 py-2 border font-mono-fin text-sm text-right"
      style={{ borderColor: "var(--ll-border)" }}
    />
  </label>
);
